/**
 * Treinamentos — a lista de cursos.
 *
 * Quem abre esta tela é alguém que acabou de ser convidado para o workspace e ainda não
 * sabe por que o número do painel não bate com o do WhatsApp. Então cada cartão responde
 * três coisas, nessa ordem: **o que o curso ensina**, **onde eu parei** e **o que ainda
 * falta subir** — porque hoje todo vídeo ainda está por gravar, e esconder isso faria a
 * pessoa clicar esperando aula em vídeo e achar que o produto quebrou.
 *
 * Três decisões que valem o comentário:
 *
 *  - **Carregando é skeleton, não `0 de 12`.** Enquanto a lista não chega, a tela não
 *    afirma progresso nenhum: `0` é medido e "não sei" é outra coisa (invariante 8).
 *  - **Curso sem aula publicada aparece assim mesmo**, dizendo que está sendo montado.
 *    Sumir com ele seria o painel decidindo por conta própria que ele não existe.
 *  - **O cartão inteiro é o link.** No celular, alvo de toque grande vale mais que um
 *    botão "abrir" de 80px no canto (DESIGN_SYSTEM seção 11).
 */
import { Link } from 'react-router';
import {
  Badge,
  Card,
  EmptyState,
  ErrorState,
  Skeleton,
  SkeletonRegion,
  SkeletonText,
} from '@mgdlead/ui';
import { GraduationCap, VideoOff } from 'lucide-react';

import { PageHeader } from '@mgdlead/ui';
import { CapituloCard } from '../../../features/guia/componentes';
import { CAPITULOS, GRUPOS_DO_GUIA, caminhoDoGuia } from '../../../features/guia/conteudo';
import { GuiaDestaque } from '../../../features/guia/GuiaDestaque';
import { useCapitulosLidos } from '../../../features/guia/progresso';
import { ProgressBar } from '../../../features/training/ProgressBar';
import {
  O_QUE_E,
  coursePath,
  progressLabel,
  progressPercent,
  semVideoHint,
} from '../../../features/training/training-ui';
import { useWorkspace } from '../../../features/workspace/WorkspaceContext';
import { useTrainingCourses } from '../../../lib/api';
import { humanizeError } from '../../../lib/errors';
import { useDocumentTitle } from '../../../lib/useDocumentTitle';

export function TrainingPage() {
  const { workspace } = useWorkspace();
  useDocumentTitle('Treinamentos');

  const cursosQuery = useTrainingCourses();
  const cursos = cursosQuery.data;
  const { lidos } = useCapitulosLidos();
  // Os capítulos "Comece aqui" aparecem já aqui; o resto mora no índice do guia.
  const primeiros = GRUPOS_DO_GUIA[0]?.capitulos ?? [];
  const numeroDe = (id: string) => CAPITULOS.findIndex((c) => c.id === id) + 1;

  return (
    <>
      <PageHeader title="Treinamentos" description={O_QUE_E} />

      {/*
        O Guia do MGDLead (texto + imagens da tela, versionado em código) vem ANTES do curso:
        ele já está completo, e o curso ainda tem aulas sem vídeo.
      */}
      <div className="mb-8 flex flex-col gap-4">
        <GuiaDestaque slug={workspace.slug} lidos={lidos} />
        <ul className="grid gap-3 sm:grid-cols-2">
          {primeiros.map((c) => (
            <li key={c.id}>
              <CapituloCard
                capitulo={c}
                numero={numeroDe(c.id)}
                href={caminhoDoGuia(workspace.slug, c.id)}
                lido={lidos.has(c.id)}
              />
            </li>
          ))}
        </ul>
      </div>

      <h2 className="mb-3 text-lg">Curso em vídeo</h2>

      {cursosQuery.isError ? (
        <Card padded={false}>
          <ErrorState
            title="Não foi possível carregar os treinamentos"
            description={humanizeError(cursosQuery.error)}
            onRetry={() => void cursosQuery.refetch()}
          />
        </Card>
      ) : cursos === undefined ? (
        <SkeletonRegion label="Carregando os cursos" className="flex flex-col gap-3">
          {[0, 1].map((linha) => (
            <Card key={linha}>
              <Skeleton className="mb-3 h-4 w-2/5" />
              <SkeletonText lines={2} />
              <Skeleton className="mt-4 h-1.5 w-full" />
            </Card>
          ))}
        </SkeletonRegion>
      ) : cursos.length === 0 ? (
        <Card padded={false}>
          <EmptyState
            kind="no-data"
            icon={GraduationCap}
            title="Nenhum curso publicado ainda"
            description="Quando o primeiro curso for publicado ele aparece aqui, e o seu progresso passa a ser guardado a partir da primeira aula que você concluir."
          />
        </Card>
      ) : (
        <ul className="flex flex-col gap-3">
          {cursos.map((curso) => {
            const aviso = semVideoHint(curso.lessonsWithoutVideo);
            return (
              <li key={curso.id}>
                <Card
                  interactive
                  className="focus-within:border-border-strong"
                  /* `asChild` não existe neste Card: o link envolve o conteúdo e recebe o foco. */
                >
                  <Link
                    to={coursePath(workspace.slug, curso.slug)}
                    className="flex flex-col gap-3 outline-none"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex flex-col gap-1">
                        <h2 className="text-md font-medium">{curso.title}</h2>
                        {curso.description ? (
                          <p className="max-w-prose text-sm text-muted-foreground">
                            {curso.description}
                          </p>
                        ) : null}
                      </div>
                      {curso.totalLessons > 0 && curso.completedLessons === curso.totalLessons ? (
                        <Badge tone="success" dot>
                          Concluído
                        </Badge>
                      ) : null}
                    </div>

                    {curso.totalLessons === 0 ? (
                      /*
                       * Curso publicado sem aula publicada. Não é `0 de 0`: seria um
                       * progresso inventado sobre um curso que ainda não tem conteúdo.
                       */
                      <p className="text-xs text-muted-foreground">
                        Este curso ainda está sendo montado — nenhuma aula publicada.
                      </p>
                    ) : (
                      <ProgressBar
                        completed={curso.completedLessons}
                        total={curso.totalLessons}
                        percent={progressPercent(curso.completedLessons, curso.totalLessons)}
                        label={progressLabel(curso.completedLessons, curso.totalLessons)}
                      />
                    )}

                    {aviso ? (
                      <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                        <VideoOff aria-hidden="true" className="mt-0.5 size-3.5 shrink-0" />
                        <span>{aviso}</span>
                      </p>
                    ) : null}
                  </Link>
                </Card>
              </li>
            );
          })}
        </ul>
      )}
    </>
  );
}
